import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalContent, ModalTitle, ModalCloseButton } from '@/ui/modal';
import { ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { getAllOrders } from '../../services/orderService';

import { useAuth } from '@hooks/useAuth';
import { useMembership } from '../../hooks/useMembership';
import { logger } from '../../utils/logger';
import ReactECharts from 'echarts-for-react';
import 'echarts-wordcloud';

const formatDateYYYYMMDDInMMT = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Yangon' }).format(d);

const WORD_CLOUD_COLORS = [
  '#822ea8',
  '#a63e9e',
  '#d4468c',
  '#ec5a9b',
  '#c337a5',
  '#ab47bc',
  '#d81b60',
  '#8e24aa',
  '#e91e63',
  '#ba68c8',
  '#f06292',
];

interface OrderItem {
  productId: string;
  name?: string | { my?: string; en?: string };
  quantity?: number;
  discountAmount?: number;
  discountType?: 'percentage' | 'fixed' | string;
  discountValue?: number;
  discountPrice?: number;
}
interface Order {
  items: OrderItem[];
  totalAmount: number;
  createdAt: string;
  payment?: { method?: string };
  status?: string;
}

interface AnalyticsAgg {
  dates: string[];
  lineSeriesRevenue: number[];
  lineSeriesOrders: number[];
  paymentPieData: Array<{ name: string; value: number }>;
  statusBarData: Array<{ name: string; value: number }>;
  wordCloudData: Array<{ name: string; value: number }>;
}

type ChartInstance = {
  getDataURL?: (opts?: { type?: 'png' | 'jpeg' | 'svg'; backgroundColor?: string }) => string;
};

export interface MonthlyDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialYear?: number;
  initialMonth?: number; // 0-based
}

export default function MonthlyDetailsDialog({
  isOpen,
  onClose,
  initialYear,
  initialMonth,
}: MonthlyDetailsDialogProps) {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { membershipInfo } = useMembership();

  const [selectedYear, setSelectedYear] = useState<number>(
    () => initialYear ?? new Date().getFullYear(),
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    () => initialMonth ?? new Date().getMonth(),
  );
  const [loading, setLoading] = useState(true);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [monthlyOrders, setMonthlyOrders] = useState<Order[]>([]);
  const [monthlyTotals, setMonthlyTotals] = useState({
    orderCount: 0,
    totalAmount: 0,
    pointsEarned: 0,
    commissionEarned: 0,
  });
  const [commissionBreakdown, setCommissionBreakdown] = useState<
    Array<{
      name: string;
      quantity: number;
      perUnit: number;
      total: number;
    }>
  >([]);
  const [commissionPerUnit, setCommissionPerUnit] = useState<Record<string, number>>({});
  logger.info('commissionPerUnit', commissionPerUnit);

  // Range-based analytics (daily/weekly/monthly)
  const [analyticsRange, setAnalyticsRange] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [analyticsAgg, setAnalyticsAgg] = useState<AnalyticsAgg | null>(null);

  const revenueChartRef = useRef<ChartInstance | null>(null);
  const ordersChartRef = useRef<ChartInstance | null>(null);
  const paymentChartRef = useRef<ChartInstance | null>(null);
  const statusChartRef = useRef<ChartInstance | null>(null);
  const wordCloudChartRef = useRef<ChartInstance | null>(null);
  const commissionChartRef = useRef<ChartInstance | null>(null);

  const getRangeDates = useCallback((range: 'daily' | 'weekly' | 'monthly') => {
    const now = new Date();
    const end = formatDateYYYYMMDDInMMT(now);
    const startDate = new Date();
    if (range === 'daily') {
      startDate.setDate(startDate.getDate());
    } else if (range === 'weekly') {
      startDate.setDate(startDate.getDate() - 6);
    } else {
      startDate.setDate(startDate.getDate() - 29);
    }
    const start = formatDateYYYYMMDDInMMT(startDate);
    return { start, end };
  }, []);

  const aggregateOrders = useCallback((orders: Order[]): AnalyticsAgg => {
    const revenueByDate: Record<string, number> = {};
    const ordersByDate: Record<string, number> = {};
    const revenueByPaymentMethod: Record<string, number> = {};
    const statusCounts: Record<string, number> = {};
    const productCounts: Record<string, number> = {};

    orders.forEach((order) => {
      const dateKey = formatDateYYYYMMDDInMMT(new Date(order.createdAt));
      revenueByDate[dateKey] = (revenueByDate[dateKey] || 0) + (order.totalAmount || 0);
      ordersByDate[dateKey] = (ordersByDate[dateKey] || 0) + 1;

      const method = (order.payment?.method || 'unknown').toUpperCase();
      revenueByPaymentMethod[method] =
        (revenueByPaymentMethod[method] || 0) + (order.totalAmount || 0);

      const status = order.status || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      (order.items || []).forEach((item) => {
        const nm = item?.name;
        const name = typeof nm === 'object' ? nm.my || nm.en || 'Unknown' : nm || 'Unknown';
        productCounts[name] = (productCounts[name] || 0) + (item?.quantity || 1);
      });
    });

    const sortedDates = Object.keys(revenueByDate).sort();
    const lineSeriesRevenue = sortedDates.map((d) => revenueByDate[d]);
    const lineSeriesOrders = sortedDates.map((d) => ordersByDate[d]);

    const paymentPieData = Object.keys(revenueByPaymentMethod).map((key) => ({
      name: key,
      value: revenueByPaymentMethod[key],
    }));
    const statusBarData = Object.keys(statusCounts).map((key) => ({
      name: key,
      value: statusCounts[key],
    }));
    const wordCloudData = Object.keys(productCounts).map((key) => ({
      name: key,
      value: productCounts[key],
    }));

    return {
      dates: sortedDates,
      lineSeriesRevenue,
      lineSeriesOrders,
      paymentPieData,
      statusBarData,
      wordCloudData,
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      const { start, end } = getRangeDates(analyticsRange);
      const filtered = allOrders.filter((order) => {
        const dateKey = formatDateYYYYMMDDInMMT(new Date(order.createdAt));
        return dateKey >= start && dateKey <= end;
      });
      setAnalyticsAgg(aggregateOrders(filtered));
    } catch (e: unknown) {
      setAnalyticsError(e instanceof Error ? e.message : 'Unknown error');
      setAnalyticsAgg(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsRange, allOrders, isOpen, isAuthenticated, aggregateOrders, getRangeDates]);

  const lineRevenueOptions = analyticsAgg
    ? {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: analyticsAgg.dates },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'Revenue',
            type: 'line',
            data: analyticsAgg.lineSeriesRevenue,
            smooth: true,
            areaStyle: {},
          },
        ],
      }
    : {};

  const lineOrdersOptions = analyticsAgg
    ? {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: analyticsAgg.dates },
        yAxis: { type: 'value' },
        series: [{ name: 'Orders', type: 'bar', data: analyticsAgg.lineSeriesOrders }],
      }
    : {};

  const paymentPieOptions = analyticsAgg
    ? {
        tooltip: { trigger: 'item' },
        series: [
          {
            name: 'Payment Revenue',
            type: 'pie',
            radius: '60%',
            data: analyticsAgg.paymentPieData,
            emphasis: {
              itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.5)' },
            },
          },
        ],
      }
    : {};

  const statusBarOptions = analyticsAgg
    ? {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: analyticsAgg.statusBarData.map((d) => d.name) },
        yAxis: { type: 'value' },
        series: [
          { name: 'Count', type: 'bar', data: analyticsAgg.statusBarData.map((d) => d.value) },
        ],
      }
    : {};

  const coloredWordCloudData = useMemo(() => {
    const getStableColor = (s: string) => {
      let hash = 0;
      for (let i = 0; i < s.length; i++) {
        hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
      }
      return WORD_CLOUD_COLORS[hash % WORD_CLOUD_COLORS.length];
    };
    return analyticsAgg
      ? analyticsAgg.wordCloudData.map((d) => ({
          ...d,
          textStyle: { color: getStableColor(d.name) },
        }))
      : [];
  }, [analyticsAgg]);

  const wordCloudOptions = analyticsAgg
    ? {
        tooltip: { show: false },
        animation: false,
        series: [
          {
            type: 'wordCloud',
            gridSize: 6,
            sizeRange: [14, 55],
            rotationRange: [-30, 30],
            rotationStep: 15,
            shape: 'circle',
            drawOutOfBound: false,
            silent: true,
            textStyle: {
              fontWeight: 'bold',
            },
            data: coloredWordCloudData,
          },
        ],
      }
    : {};

  const availableYears = useMemo(() => {
    const list = Array.from(
      new Set(allOrders.map((o) => new Date(o.createdAt).getFullYear())),
    ).sort((a, b) => b - a);
    return list.length > 0 ? list : [selectedYear];
  }, [allOrders, selectedYear]);

  const handlePrevMonth = useCallback(() => {
    setSelectedMonth((m) => {
      if (m === 0) {
        setSelectedYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setSelectedMonth((m) => {
      if (m === 11) {
        setSelectedYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  // Fetch orders when dialog opens
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    // Initialize selected date from props each time it opens
    const today = new Date();
    setSelectedYear(initialYear ?? today.getFullYear());
    setSelectedMonth(initialMonth ?? today.getMonth());

    const fetchAll = async () => {
      try {
        setLoading(true);
        let page = 1;
        const limit = 0;
        let orders: Order[] = [];
        let totalPages = 1;

        const firstResp = await getAllOrders(page, limit, 'completed');
        if (firstResp.success) {
          const ordersData = Array.isArray(firstResp.data) ? (firstResp.data as Order[]) : [];
          totalPages = Number(firstResp.meta?.pages ?? 1);
          orders = ordersData;
        }
        for (page = 2; page <= totalPages; page++) {
          try {
            const resp = await getAllOrders(page, limit, 'completed');
            if (resp.success) {
              const pageOrders = Array.isArray(resp.data) ? (resp.data as Order[]) : [];
              orders = orders.concat(pageOrders);
            }
          } catch (e) {
            logger.warn('Monthly details page fetch failed:', e);
          }
        }
        setAllOrders(orders);
      } catch (error) {
        logger.error('Failed to fetch orders (MonthlyDetailsDialog):', error);
        setAllOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [isOpen, isAuthenticated, initialYear, initialMonth]);

  // Filter monthly orders by selected year/month
  useEffect(() => {
    const monthOrders = allOrders.filter((o) => {
      const d = new Date(o.createdAt);
      return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
    });
    setMonthlyOrders(monthOrders);
  }, [allOrders, selectedYear, selectedMonth]);

  // Compute totals, points or commission, and word cloud
  useEffect(() => {
    const compute = async () => {
      try {
        setLoading(true);
        const orderCount = monthlyOrders.length;
        const totalAmount = monthlyOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

        const tierName = membershipInfo?.currentTier?.name?.toLowerCase();
        const pointsRatePerThousand = tierName === 'gold' ? 15 : tierName === 'silver' ? 10 : 5;
        const pointsEarned = Math.floor(totalAmount / 1000) * pointsRatePerThousand;

        let commissionEarned = 0;
        const isMerchant = user?.activeRole === 'merchant';
        if (isMerchant) {
          // Use per-item discount as commission
          const breakdownMap = new Map<
            string,
            { name: string; quantity: number; perUnit: number; total: number }
          >();
          monthlyOrders.forEach((o) => {
            o.items.forEach((it) => {
              const nm = it.name;
              const rawName = typeof nm === 'object' ? nm?.my || nm?.en || '' : nm || '';
              const displayName = rawName.trim() || 'Unknown Item';
              const key = it.productId || displayName.toLowerCase();
              const perUnit = Number(it.discountAmount) || 0;
              const qty = it.quantity || 1;
              commissionEarned += perUnit * qty;

              const existing = breakdownMap.get(key);
              if (existing) {
                existing.quantity += qty;
                existing.total += perUnit * qty;
                breakdownMap.set(key, existing);
              } else {
                breakdownMap.set(key, {
                  name: displayName,
                  quantity: qty,
                  perUnit,
                  total: perUnit * qty,
                });
              }
            });
          });
          const breakdownArr = Array.from(breakdownMap.values()).sort((a, b) => b.total - a.total);
          setCommissionBreakdown(breakdownArr);
          setCommissionPerUnit({}); // discount varies per order; compute daily directly
        } else {
          setCommissionPerUnit({});
          setCommissionBreakdown([]);
        }

        const itemFreq = new Map<string, { name: string; count: number }>();
        monthlyOrders.forEach((o) => {
          o.items.forEach((it) => {
            const nm = it.name;
            const rawName = typeof nm === 'object' ? nm?.my || nm?.en || '' : nm || '';
            const displayName = rawName.trim() || 'Unknown Item';
            const key = it.productId || displayName.toLowerCase();
            const qty = it.quantity || 1;
            const current = itemFreq.get(key);
            if (current) {
              itemFreq.set(key, { name: current.name, count: current.count + qty });
            } else {
              itemFreq.set(key, { name: displayName, count: qty });
            }
          });
        });
        setMonthlyTotals({ orderCount, totalAmount, pointsEarned, commissionEarned });
        // Do not auto-close when no data; show placeholder in UI instead
      } catch (error) {
        logger.error('Failed to compute analytics (MonthlyDetailsDialog):', error);
        setMonthlyTotals({ orderCount: 0, totalAmount: 0, pointsEarned: 0, commissionEarned: 0 });
        setCommissionBreakdown([]);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) compute();
  }, [monthlyOrders, membershipInfo, isAuthenticated, isOpen, onClose, user]);

  const dailyMetrics = useMemo(() => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const totals: Array<{ day: number; amount: number; count: number; commission: number }> = [];
    let maxAmount = 0;
    let maxCount = 0;
    let maxCommission = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const dayOrders = monthlyOrders.filter((o) => {
        const d = new Date(o.createdAt);
        return (
          d.getFullYear() === selectedYear && d.getMonth() === selectedMonth && d.getDate() === day
        );
      });
      const amount = dayOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
      const count = dayOrders.length;
      let commission = 0;
      if (user?.activeRole === 'merchant') {
        dayOrders.forEach((o) =>
          (o.items || []).forEach((it) => {
            const perUnit = Number(it.discountAmount) || 0;
            commission += perUnit * (it.quantity || 1);
          }),
        );
      }
      totals.push({ day, amount, count, commission });
      maxAmount = Math.max(maxAmount, amount);
      maxCount = Math.max(maxCount, count);
      maxCommission = Math.max(maxCommission, commission);
    }

    return { totals, maxAmount, maxCount, maxCommission };
  }, [monthlyOrders, selectedYear, selectedMonth, user?.activeRole]);

  const bestSalesDay = useMemo(() => {
    if (!dailyMetrics || dailyMetrics.maxAmount === 0 || dailyMetrics.totals.length === 0)
      return null;
    let idx = 0;
    for (let i = 1; i < dailyMetrics.totals.length; i++) {
      if (dailyMetrics.totals[i].amount > dailyMetrics.totals[idx].amount) idx = i;
    }
    return { day: idx + 1, amount: dailyMetrics.totals[idx].amount };
  }, [dailyMetrics]);

  const lineCommissionOptions =
    user?.activeRole === 'merchant' && dailyMetrics
      ? {
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: dailyMetrics.totals.map((d) => d.day) },
          yAxis: { type: 'value' },
          series: [
            {
              name: i18n.language === 'mm' ? 'ကော်မရှင်' : 'Commission',
              type: 'line',
              data: dailyMetrics.totals.map((d) => d.commission),
              smooth: true,
              areaStyle: {},
              color: '#10b981',
            },
          ],
        }
      : {};

  const handlePrint = useCallback(() => {
    try {
      const monthLabel = new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
        i18n.language === 'mm' ? 'my-MM' : 'en-US',
        { month: 'long', year: 'numeric' },
      );

      const revUrl = revenueChartRef.current?.getDataURL?.({
        type: 'png',
        backgroundColor: '#ffffff',
      });
      const ordUrl = ordersChartRef.current?.getDataURL?.({
        type: 'png',
        backgroundColor: '#ffffff',
      });
      const payUrl = paymentChartRef.current?.getDataURL?.({
        type: 'png',
        backgroundColor: '#ffffff',
      });
      const statUrl = statusChartRef.current?.getDataURL?.({
        type: 'png',
        backgroundColor: '#ffffff',
      });
      const wcUrl = wordCloudChartRef.current?.getDataURL?.({
        type: 'png',
        backgroundColor: '#ffffff',
      });

      const origin = window.location.origin;
      const logoSrc = `${origin}/logo-new.svg`;

      const dailyRows = dailyMetrics.totals
        .map(
          (d) => `
        <tr>
          <td>${d.day}</td>
          <td style="text-align:right">${(d.amount || 0).toLocaleString()} MMK</td>
          <td style="text-align:right">${d.count || 0}</td>
        </tr>
      `,
        )
        .join('');

      const commissionRows =
        user?.activeRole === 'merchant' && commissionBreakdown.length
          ? commissionBreakdown
              .map(
                (r) => `
          <tr>
            <td>${r.name}</td>
            <td style="text-align:right">${r.quantity}</td>
            <td style="text-align:right">${r.perUnit.toLocaleString()} MMK</td>
            <td style="text-align:right">${r.total.toLocaleString()} MMK</td>
          </tr>
        `,
              )
              .join('')
          : '';

      const totalsBlock =
        user?.activeRole === 'merchant'
          ? `
          <div class="card">
            <div class="label">${i18n.language === 'mm' ? 'ကော်မရှင်' : 'Commission'}</div>
            <div class="value">${monthlyTotals.commissionEarned.toLocaleString()} MMK</div>
          </div>
        `
          : `
          <div class="card">
            <div class="label">${i18n.language === 'mm' ? 'ရရှိသော အမှတ်' : 'Points Earned'}</div>
            <div class="value">${monthlyTotals.pointsEarned.toLocaleString()}</div>
          </div>
        `;

      const chartImg = (url?: string, title?: string) =>
        url
          ? `<div class="chart"><div class="chart-title">${title || ''}</div><img src="${url}" /></div>`
          : '';

      const style = `
        <style>
          @page { size: A4; margin: 12mm; }
          body { font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Myanmar Sangam MN', 'Myanmar Text', sans-serif; color: #111827; }
          h1 { font-size: 22px; margin: 0 0 12px; }
          .sub { color: #6b7280; font-size: 12px; margin-bottom: 16px; }
          .brand { display: flex; justify-content: space-between; margin-bottom: 12px; }
          .brand-logo { height: 24px; width: auto !important; border-radius: 0; }
          .powered { font-size: 12px; color: #6b7280; }
          .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
          .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
          .label { font-size: 12px; color: #6b7280; }
          .value { font-size: 18px; font-weight: 600; }
          .charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
          .chart { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; }
          .chart-title { font-size: 12px; color: #374151; margin-bottom: 6px; }
          .table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          .table th, .table td { border: 1px solid #e5e7eb; padding: 8px; font-size: 12px; }
          .table th { background: #f9fafb; text-align: left; }
          .muted { color: #6b7280; }
          img { width: 100%; border-radius: 6px; }
        </style>
      `;

      const headerTitle =
        i18n.language === 'mm' ? 'လစဉ်အသေးစိတ်အစီရင်ခံစာ' : 'Monthly Details Report';

      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            ${style}
            <title>${headerTitle} — ${monthLabel}</title>
          </head>
          <body>
            <div class="brand">
              <img class="brand-logo" src="${logoSrc}" alt="EZ TopUp Logo" />
              <div class="powered">Powered by EZTopUp</div>
            </div>

            <h1>${headerTitle}</h1>
            <div class="sub">${monthLabel}</div>

            <div class="grid">
              <div class="card">
                <div class="label">${i18n.language === 'mm' ? 'ဒီလ' : 'This Month'}</div>
                <div class="value">${monthlyTotals.totalAmount.toLocaleString()} MMK</div>
                <div class="muted">${monthlyTotals.orderCount} ${i18n.language === 'mm' ? 'အော်ဒါ' : 'orders'}</div>
              </div>
              ${totalsBlock}
              <div class="card">
                <div class="label">${i18n.language === 'mm' ? 'အကောင်းဆုံးနေ့' : 'Top Day'}</div>
                <div class="value">${bestSalesDay ? bestSalesDay.day : '-'}</div>
                <div class="muted">${i18n.language === 'mm' ? 'ရောင်းအားအမြင့်ဆုံး' : 'Highest revenue'}</div>
              </div>
            </div>

            <div class="charts">
              ${chartImg(revUrl, i18n.language === 'mm' ? 'ဝင်ငွေ အရွယ်အစား' : 'Revenue Trend')}
              ${chartImg(ordUrl, i18n.language === 'mm' ? 'အော်ဒါ အရေအတွက်' : 'Orders Count')}
              ${chartImg(payUrl, i18n.language === 'mm' ? 'ငွေပေးချေမှုနည်းလမ်းအလိုက် ဝင်ငွေ' : 'Revenue by Payment')}
              ${chartImg(statUrl, i18n.language === 'mm' ? 'အော်ဒါအခြေအနေ' : 'Order Status Distribution')}
            </div>

            ${chartImg(wcUrl, i18n.language === 'mm' ? 'ထိပ်ဆုံး ကုန်ပစ္စည်း Word Cloud' : 'Top Products Word Cloud')}

            ${
              user?.activeRole === 'merchant' && commissionRows
                ? `
            <table class="table">
              <thead>
                <tr>
                  <th>${i18n.language === 'mm' ? 'ကုန်ပစ္စည်း' : 'Product'}</th>
                  <th style="text-align:right">${i18n.language === 'mm' ? 'အရေအတွက်' : 'Quantity'}</th>
                  <th style="text-align:right">${i18n.language === 'mm' ? 'နှစ်ခုလျှင် ကော်မရှင်' : 'Per-unit Commission'}</th>
                  <th style="text-align:right">${i18n.language === 'mm' ? 'စုစုပေါင်း (MMK)' : 'Total (MMK)'}</th>
                </tr>
              </thead>
              <tbody>
                ${commissionRows}
              </tbody>
            </table>
            `
                : ''
            }

            <table class="table">
              <thead>
                <tr>
                  <th>${i18n.language === 'mm' ? 'နေ့' : 'Day'}</th>
                  <th>${i18n.language === 'mm' ? 'MMK' : 'Amount (MMK)'}</th>
                  <th>${i18n.language === 'mm' ? 'အော်ဒါ' : 'Orders'}</th>
                </tr>
              </thead>
              <tbody>
                ${dailyRows}
              </tbody>
            </table>
          <script>
            window.addEventListener('load', function(){
              setTimeout(function(){ window.print(); }, 400);
            });
          </script>
          </body>
        </html>
      `;

      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) return;
      setTimeout(() => {
        try {
          win.focus();
        } catch (e) {
          logger.warn('Failed to focus print window:', e);
        }
      }, 200);
    } catch (err) {
      logger.error('Print failed:', err);
    }
  }, [
    selectedYear,
    selectedMonth,
    i18n.language,
    monthlyTotals,
    bestSalesDay,
    dailyMetrics,
    commissionBreakdown,
    user?.activeRole,
  ]);

  const bestOrdersDay = useMemo(() => {
    if (!dailyMetrics || dailyMetrics.maxCount === 0 || dailyMetrics.totals.length === 0)
      return null;
    let idx = 0;
    for (let i = 1; i < dailyMetrics.totals.length; i++) {
      if (dailyMetrics.totals[i].count > dailyMetrics.totals[idx].count) idx = i;
    }
    return { day: idx + 1, count: dailyMetrics.totals[idx].count };
  }, [dailyMetrics]);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Date(1970, 0, 4 + i).toLocaleDateString(i18n.language === 'mm' ? 'my-MM' : 'en-US', {
          weekday: 'short',
        }),
      ),
    [i18n.language],
  );

  const firstWeekday = useMemo(
    () => new Date(selectedYear, selectedMonth, 1).getDay(),
    [selectedYear, selectedMonth],
  );

  const daysInMonthUI = useMemo(
    () => new Date(selectedYear, selectedMonth + 1, 0).getDate(),
    [selectedYear, selectedMonth],
  );

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      className="h-full max-h-[95vh] flex flex-col"
    >
      <ModalHeader className="relative">
        <ModalTitle>{i18n.language === 'mm' ? 'လစဉ်အသေးစိတ်' : 'Monthly Details'}</ModalTitle>
        <ModalCloseButton onClose={onClose} />
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <button onClick={handlePrevMonth} className="p-2 rounded hover:bg-gray-100">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-gray-700">
            {new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
              i18n.language === 'mm' ? 'my-MM' : 'en-US',
              { month: 'long', year: 'numeric' },
            )}
          </span>
          <button onClick={handleNextMonth} className="p-2 rounded hover:bg-gray-100">
            <ChevronRight className="w-5 h-5" />
          </button>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white text-gray-700 hover:bg-gray-50"
            aria-label={i18n.language === 'mm' ? 'နှစ်ရွေးချယ်ရန်' : 'Select Year'}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white text-gray-700 hover:bg-gray-50"
            aria-label={i18n.language === 'mm' ? 'လရွေးချယ်ရန်' : 'Select Month'}
          >
            {Array.from({ length: 12 }, (_, m) => (
              <option key={m} value={m}>
                {new Date(selectedYear, m, 1).toLocaleString(
                  i18n.language === 'mm' ? 'my-MM' : 'en-US',
                  { month: 'long' },
                )}
              </option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
            aria-label={i18n.language === 'mm' ? 'ပုံနှိပ်' : 'Print Report'}
          >
            <Printer className="w-4 h-4" />
            {i18n.language === 'mm' ? 'ပုံနှိပ်' : 'Print Report'}
          </button>
        </div>
      </ModalHeader>
      <ModalContent>
        {loading ? (
          <div className="p-6">
            <div className="py-54 text-center">
              <div
                className="mx-auto mb-2 w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"
                aria-label={i18n.language === 'mm' ? 'Loading လုပ်နေသည်' : 'Loading'}
              ></div>
              <p className="text-sm text-gray-700">
                {i18n.language === 'mm' ? 'Loading လုပ်နေသည်' : 'Loading...'}
              </p>
            </div>
          </div>
        ) : monthlyTotals.orderCount === 0 ? (
          <div className="p-6">
            <div className="py-58 text-center">
              <p className="text-sm text-gray-700">
                {i18n.language === 'mm' ? 'ဒေတာ မရှိပါ' : 'No data found'}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6 h-full overflow-y-auto">
            {/* Totals */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
              <div className="p-4 rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors">
                <p className="text-xs text-gray-500">{t('common.thisMonth')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {monthlyTotals.totalAmount.toLocaleString()} MMK
                </p>
                <p className="text-xs text-gray-600">
                  {monthlyTotals.orderCount} {t('orderHistory.orders')}
                </p>
              </div>
              {user?.activeRole === 'merchant' ? (
                <div className="p-4 rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors">
                  <p className="text-xs text-gray-500">
                    {i18n.language === 'mm' ? 'ကော်မရှင်' : 'Commission'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {monthlyTotals.commissionEarned.toLocaleString()} MMK
                  </p>
                </div>
              ) : (
                <div className="p-4 rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors">
                  <p className="text-xs text-gray-500">
                    {i18n.language === 'mm' ? 'ရရှိသော အမှတ်' : 'Points Earned'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {monthlyTotals.pointsEarned.toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Sales Analytics */}
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-sm font-medium">
                  {i18n.language === 'mm' ? 'အချိန်ရွေးချယ်ရန်:' : 'Time Range:'}
                </label>
                <select
                  value={analyticsRange}
                  onChange={(e) =>
                    setAnalyticsRange(e.target.value as 'daily' | 'weekly' | 'monthly')
                  }
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="daily">{i18n.language === 'mm' ? 'နေ့စဉ်' : 'Daily'}</option>
                  <option value="weekly">{i18n.language === 'mm' ? 'ရက်သပိတ်' : 'Weekly'}</option>
                  <option value="monthly">{i18n.language === 'mm' ? 'လစဉ်' : 'Monthly'}</option>
                </select>
                {analyticsLoading && (
                  <span className="text-sm text-gray-500">
                    {i18n.language === 'mm' ? 'Loading...' : 'Loading...'}
                  </span>
                )}
                {analyticsError && <span className="text-sm text-red-600">{analyticsError}</span>}
              </div>

              {analyticsAgg && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-semibold mb-2">
                      {i18n.language === 'mm'
                        ? 'ရောင်းဝယ်မှု အဆင့်ဖော်ပြချက် (MMK)'
                        : 'Revenue Trend (MMK)'}
                    </div>
                    <ReactECharts
                      option={lineRevenueOptions}
                      style={{ height: 300 }}
                      onChartReady={(inst) => (revenueChartRef.current = inst)}
                    />
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-semibold mb-2">
                      {i18n.language === 'mm' ? 'နေ့စဉ် အော်ဒါရေတွက်' : 'Orders Count by Day'}
                    </div>
                    <ReactECharts
                      option={lineOrdersOptions}
                      style={{ height: 300 }}
                      onChartReady={(inst) => (ordersChartRef.current = inst)}
                    />
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-semibold mb-2">
                      {i18n.language === 'mm'
                        ? 'ငွေပေးချေမှုနည်းလမ်းအလိုက် ဝင်ငွေ'
                        : 'Revenue by Payment'}
                    </div>
                    <ReactECharts
                      option={paymentPieOptions}
                      style={{ height: 300 }}
                      onChartReady={(inst) => (paymentChartRef.current = inst)}
                    />
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-semibold mb-2">
                      {i18n.language === 'mm'
                        ? 'အော်ဒါအခြေအနေ ဖြန့်ဖြူးမှု'
                        : 'Orders Status Distribution'}
                    </div>
                    <ReactECharts
                      option={statusBarOptions}
                      style={{ height: 300 }}
                      onChartReady={(inst) => (statusChartRef.current = inst)}
                    />
                  </div>

                  <div className="xl:col-span-2 bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-semibold mb-2">
                      {i18n.language === 'mm'
                        ? 'ထိပ်ဆုံးကုန်ပစ္စည်း — Word Cloud'
                        : 'Top Products — Word Cloud'}
                    </div>
                    <ReactECharts
                      option={wordCloudOptions}
                      style={{ height: 350 }}
                      notMerge={false}
                      lazyUpdate
                      opts={{ renderer: 'svg' }}
                      onChartReady={(inst) => (wordCloudChartRef.current = inst)}
                    />
                  </div>

                  {user?.activeRole === 'merchant' && (
                    <div className="xl:col-span-2 bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm font-semibold mb-2">
                        {i18n.language === 'mm'
                          ? 'ကော်မရှင် — နေ့ချင်း လိုင်းပြဇယား'
                          : 'Commission — Daily Line Chart'}
                      </div>
                      <ReactECharts
                        option={lineCommissionOptions}
                        style={{ height: 300 }}
                        onChartReady={(inst) => (commissionChartRef.current = inst)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {user?.activeRole === 'merchant' && commissionBreakdown.length > 0 && (
              <div className="p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="sm:flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-900">
                    {i18n.language === 'mm' ? 'ကော်မရှင် အကျဉ်းချုပ်' : 'Commission Breakdown'}
                  </p>
                  <span className="text-xs text-gray-600">
                    {i18n.language === 'mm' ? 'ကုန်ပစ္စည်းအလိုက်' : 'By product'}
                  </span>
                </div>
                <div className="overflow-x-auto -mx-2">
                  <table className="min-w-[740px] w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-2 py-2 border border-gray-200">
                          {i18n.language === 'mm' ? 'ကုန်ပစ္စည်း' : 'Product'}
                        </th>
                        <th className="text-right px-2 py-2 border border-gray-200">
                          {i18n.language === 'mm' ? 'အရေအတွက်' : 'Quantity'}
                        </th>
                        <th className="text-right px-2 py-2 border border-gray-200">
                          {i18n.language === 'mm' ? 'နှစ်ခုလျှင် ကော်မရှင်' : 'Per-unit Commission'}
                        </th>
                        <th className="text-right px-2 py-2 border border-gray-200">
                          {i18n.language === 'mm' ? 'စုစုပေါင်း (MMK)' : 'Total (MMK)'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissionBreakdown.map((row, idx) => (
                        <tr key={`cb-${idx}`}>
                          <td className="px-2 py-2 border border-gray-200 text-gray-900">
                            {row.name}
                          </td>
                          <td className="px-2 py-2 border border-gray-200 text-right">
                            {row.quantity}
                          </td>
                          <td className="px-2 py-2 border border-gray-200 text-right">
                            {row.perUnit.toLocaleString()} MMK
                          </td>
                          <td className="px-2 py-2 border border-gray-200 text-right font-semibold">
                            {row.total.toLocaleString()} MMK
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Daily Breakdown as Calendar */}
            <div className="sm:flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-900">
                {i18n.language === 'mm' ? 'နေ့ချင်းအကျဉ်းချုပ်' : 'Daily Breakdown'}
              </p>
              {user?.activeRole === 'merchant' && (
                <div className="sm:flex items-center gap-3">
                  <span className="text-xs text-gray-600">
                    {dailyMetrics.maxAmount > 0
                      ? `${i18n.language === 'mm' ? 'ရောင်းအားအကောင်းဆုံး' : 'Best Sales'}: ${bestSalesDay?.day ?? '-'}`
                      : ''}
                    {dailyMetrics.maxAmount > 0 && dailyMetrics.maxCount > 0 ? ' • ' : ''}
                    {dailyMetrics.maxCount > 0
                      ? `${i18n.language === 'mm' ? 'အော်ဒါအများဆုံး' : 'Most Orders'}: ${bestOrdersDay?.day ?? '-'}`
                      : ''}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                      <span className="w-3 h-2 rounded bg-blue-600" />{' '}
                      {i18n.language === 'mm' ? 'ရောင်းအား' : 'Sales'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                      <span className="w-3 h-2 rounded bg-amber-500" />{' '}
                      {i18n.language === 'mm' ? 'အော်ဒါ' : 'Orders'}
                    </span>
                    {user?.activeRole === 'merchant' && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                        <span className="w-3 h-2 rounded bg-emerald-500" />{' '}
                        {i18n.language === 'mm' ? 'ကော်မရှင်' : 'Commission'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 rounded-lg border border-gray-200 shadow-sm">
              <div className="overflow-x-auto -mx-2">
                <div className="min-w-[1200px] px-2">
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {weekDays.map((wd, idx) => (
                      <div
                        key={`wd-${idx}`}
                        className="text-xs font-medium text-gray-500 text-center"
                      >
                        {wd}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {Array.from({ length: firstWeekday }).map((_, idx) => (
                      <div
                        key={`blank-${idx}`}
                        className="min-h-[72px] border border-transparent rounded"
                      ></div>
                    ))}
                    {Array.from({ length: daysInMonthUI }).map((_, idx) => {
                      const day = idx + 1;
                      const t = dailyMetrics.totals[day - 1];
                      const hasData = (t?.count ?? 0) > 0 || (t?.amount ?? 0) > 0;
                      const salesIntensity = dailyMetrics.maxAmount
                        ? (t?.amount ?? 0) / dailyMetrics.maxAmount
                        : 0;
                      const ordersIntensity = dailyMetrics.maxCount
                        ? (t?.count ?? 0) / dailyMetrics.maxCount
                        : 0;
                      const compositeIntensity = 0.6 * salesIntensity + 0.4 * ordersIntensity;
                      const bgColor = `rgba(59, 130, 246, ${0.08 + compositeIntensity * 0.22})`;
                      const isBest = user?.activeRole === 'merchant' && bestSalesDay?.day === day;
                      const today = new Date();
                      const isToday =
                        today.getFullYear() === selectedYear &&
                        today.getMonth() === selectedMonth &&
                        today.getDate() === day;
                      return (
                        <div
                          key={`daycell-${day}`}
                          className={`group relative min-h-[84px] rounded border p-2 active:scale-[0.98] transition-transform flex flex-col gap-1 border-gray-200 justify-between ${hasData ? '' : 'bg-gray-50'}`}
                          style={{ backgroundColor: hasData ? bgColor : undefined }}
                          title={`${new Date(selectedYear, selectedMonth, day).toLocaleDateString(i18n.language === 'mm' ? 'my-MM' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })} • ${(t?.amount ?? 0).toLocaleString()} MMK • ${t?.count ?? 0} ${i18n.language === 'mm' ? 'အော်ဒါ' : 'orders'}${user?.activeRole === 'merchant' ? ` • ${(t?.commission ?? 0).toLocaleString()} MMK ${i18n.language === 'mm' ? 'ကော်မရှင်' : 'commission'}` : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div
                              className={`text-xs ${hasData ? 'text-gray-800' : 'text-gray-400'}`}
                            >
                              {day}
                            </div>
                            <div className="flex items-center gap-1">
                              {isBest && (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500 text-white">
                                  {i18n.language === 'mm' ? 'အကောင်းဆုံး' : 'Top'}
                                </span>
                              )}
                              {isToday && (
                                <span className="text-[10px] px-1 py-0.5 rounded border border-blue-600 text-blue-700 bg-white/80">
                                  {i18n.language === 'mm' ? 'ယနေ့' : 'Today'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="w-full h-2 rounded bg-white/60">
                              <div
                                className="h-2 rounded bg-blue-600"
                                style={{ width: `${Math.round(salesIntensity * 100)}%` }}
                              />
                            </div>
                            <div className="w-full h-2 rounded bg-white/60">
                              <div
                                className="h-2 rounded bg-amber-500"
                                style={{ width: `${Math.round(ordersIntensity * 100)}%` }}
                              />
                            </div>
                            {user?.activeRole === 'merchant' && (
                              <div className="w-full h-2 rounded bg-white/60">
                                <div
                                  className="h-2 rounded bg-emerald-500"
                                  style={{
                                    width: `${Math.round((dailyMetrics.maxCommission ? (t?.commission ?? 0) / dailyMetrics.maxCommission : 0) * 100)}%`,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="mt-1 flex items-center justify-between">
                            <span className="text-[11px] font-medium text-gray-900">
                              {(t?.amount ?? 0).toLocaleString()} MMK
                            </span>
                            <span className="text-[11px] text-gray-700">
                              {t?.count ?? 0}{' '}
                              {t?.count === 1
                                ? i18n.language === 'mm'
                                  ? 'အော်ဒါ'
                                  : 'order'
                                : i18n.language === 'mm'
                                  ? 'အော်ဒါများ'
                                  : 'orders'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
